<template>
	<div class="compensation-box-content">
		<div class="compensation-box-container">
			<p>{{ deliveryText }}</p>
			<div class="compensation-box-rewards">
				<BidirectionalTextLabel
					:text="translate('ui.rewards')"
					:theme="LabelThemes.GREEN"
					class="rewards-label"
				/>
				<div class="compensation-box-rewards__content">
					<div
						v-if="buildingRewards"
						class="compensation-box-rewards__row"
					>
						<Tooltip
							v-for="building in buildingRewards"
							:key="
								(building.rewards as InGameRewardBuilding)
									.frameName
							"
							:placement="Placements.TOP"
						>
							<template #element>
								<AppItemAmount
									:amount="building.rewards.amount"
									:frameName="
										(
											building.rewards as InGameRewardBuilding
										).frameName
									"
									:iconSize="20"
								/>
							</template>
							<template #tooltip>
								<span>
									{{
										translate(
											`${(building.rewards as InGameRewardBuilding).frameName}.name`,
										)
									}}
								</span>
							</template>
						</Tooltip>
					</div>
					<div
						v-if="currencyRewards"
						class="compensation-box-rewards__row"
					>
						<Tooltip
							v-for="currency in currencyRewards"
							:key="
								(currency.rewards as InGameRewardCurrency)
									.currency
							"
							:placement="Placements.TOP"
						>
							<template #element>
								<AppPrice
									:amount="currency.rewards.amount"
									:currency="
										resourceTypeToCurrencyType(
											(
												currency.rewards as InGameRewardCurrency
											).currency,
										)
									"
									:resourceCheck="false"
									:resourceChangeHighlight="
										ResourceChangeHighlight.POSITIVE
									"
									:iconSize="16"
								/>
							</template>
							<template #tooltip>
								<span>
									{{
										translate(
											`resource.${resourceTypeToCurrencyType(
												(
													currency.rewards as InGameRewardCurrency
												).currency,
											)}`,
										)
									}}
								</span>
							</template>
						</Tooltip>
					</div>
				</div>
				<RectangleBoxContainer />
			</div>
		</div>
		<AppTextButton
			:type="StatusTypes.SUCCESS"
			:text="translate('button.claim')"
			@click="handleClaimRewards"
		/>
	</div>
</template>

<script lang="ts" setup>
import AppItemAmount from "@/components/AppItemAmount.vue";
import AppPrice from "@/components/AppPrice.vue";
import AppTextButton from "@/components/AppTextButton.vue";
import BidirectionalTextLabel from "@/components/BidirectionalTextLabel.vue";
import RectangleBoxContainer from "@/components/SVG/RectangleBoxContainer.vue";
import Tooltip from "@/components/Tooltip.vue";
import useGameTicker from "@/hooks/useGameTicker";
import useRewardClaimGuard from "@/hooks/useRewardClaimGuard";
import useSocketService from "@/hooks/useSocketService";
import type {
	InGameReward,
	InGameRewardBuilding,
	InGameRewardCurrency,
} from "@/libs/SocketService";
import { EmitStores } from "@/libs/SocketService";
import useBuildingsStorageStore from "@/store/useBuildingsStorageStore";
import { usePopupStore } from "@/store/usePopupStore";
import useResourcesStore from "@/store/useResourcesStore";
import useRewardStore from "@/store/useRewardStore";
import useUpgradesStore from "@/store/useUpgradesStore";
import {
	LabelThemes,
	Placements,
	ResourceChangeHighlight,
	StatusTypes,
} from "@/types/ui";
import { resourceTypeToCurrencyType } from "@/utils/formatPrice";
import { translate } from "@/utils/translate";
import { storeToRefs } from "pinia";
import { computed, markRaw } from "vue";

const claimGuard = useRewardClaimGuard();

const ticker = markRaw(useGameTicker());
const socketService = markRaw(useSocketService());
const resourcesStore = useResourcesStore();
const buildingsStorageStore = useBuildingsStorageStore();
const rewardStore = useRewardStore();
const popupStore = usePopupStore();
const { inGameCompensation } = storeToRefs(rewardStore);

const buildingRewards = computed(
	(): Array<InGameReward> | null =>
		inGameCompensation.value?.filter(
			(reward: InGameReward): boolean =>
				!!(reward?.rewards as InGameRewardBuilding)?.frameName,
		) ?? null,
);

const currencyRewards = computed(
	(): Array<InGameReward> | null =>
		inGameCompensation.value?.filter(
			(reward: InGameReward): boolean =>
				!!(reward?.rewards as InGameRewardCurrency)?.currency,
		) ?? null,
);

// Prefer admin broadcast note when present, else default copy
const deliveryText = computed((): string => {
	const custom = inGameCompensation.value
		?.map((reward: InGameReward) => reward.message?.trim() || "")
		.find((msg: string) => msg.length > 0);
	return custom || translate("ui.special_delivery_text");
});

const handleClaimRewards = (): Promise<void> =>
	claimGuard.guard(async (): Promise<void> => {
		const ids: Array<string> = [];
		let hasUpgradeRefund = false;
		ticker.stop();

		if (buildingRewards.value) {
			Object.entries(buildingRewards.value).forEach(
				([, building]: [string, InGameReward]): void => {
					const buildingReward =
						building?.rewards as InGameRewardBuilding;
					buildingsStorageStore.increase(
						buildingReward.frameName,
						buildingReward.amount,
						"building compensation reward claim",
					);
					ids.push(building._id);
				},
			);
		}

		if (currencyRewards.value) {
			Object.entries(currencyRewards.value).forEach(
				([, currency]: [string, InGameReward]): void => {
					const currencyReward =
						currency?.rewards as InGameRewardCurrency;
					resourcesStore.increase(
						currencyReward.currency,
						currencyReward.amount,
						typeof currencyReward.upgradeId === "number"
							? currency.message ||
									"resource compensation reward claim"
							: "resource compensation reward claim",
					);
					const upgradeId = currencyReward.upgradeId;
					if (typeof upgradeId === "number") {
						hasUpgradeRefund = true;
						useUpgradesStore().setUpgradeLevelById(
							upgradeId,
							Math.max(
								0,
								useUpgradesStore().getCurrentLevelById(
									upgradeId,
								) - 1,
							),
						);
					}
					ids.push(currency._id);
				},
			);
		}

		await Promise.all([
			socketService.emitSaveRequest({
				stores: [
					EmitStores.BUILDINGS_STORAGE,
					EmitStores.RESOURCES,
					...(hasUpgradeRefund ? [EmitStores.UPGRADES] : []),
				],
			}),
		]);

		await socketService.emitClaimCompensationRewards({ ids: ids });
		await socketService.getInGameCompensationRewards();

		if (!ticker.isLocked) ticker.start();

		popupStore.hidePopup();
	});
</script>

<style lang="scss" scoped>
.compensation-box {
	&-content {
		position: relative;
		@include flex-col-center;
		gap: 0.75rem;
	}

	&-container {
		@include flex-col-center;
		position: relative;
		gap: 1rem;
		box-shadow: inset 0 0 0.75rem 0 rgba(29, 30, 33, 0.8);
		border-width: 0.05rem;
		border-style: solid;
		border-image-source: linear-gradient(to top, #836e49, #bfa370);
		border-image-slice: 1;
		background-image: linear-gradient(to top, #ab8849, #f2dcb5);
		padding: 0.75rem;
		width: 100%;

		span {
			color: #372615 !important;
			font-size: 0.813rem;
			text-shadow: none !important;
		}

		&::before {
			position: absolute;
			top: 0;
			left: 0;
			opacity: 1;
			mix-blend-mode: multiply;
			background: url($path-images + "backgrounds/textures/marble-texture-bg.avif")
				repeat center;
			background-size: 40%;
			width: 100%;
			height: 100%;
			content: "";
		}
	}

	&-rewards {
		width: 100%;
		@include flex-row-center;
		position: relative;
		margin-top: 1rem;
		background-image: linear-gradient(
			to top,
			rgb(37, 38, 42),
			rgb(66, 67, 74)
		);

		&::before {
			position: absolute;
			top: 0;
			left: 0;
			opacity: 0.6;
			mix-blend-mode: color-burn;
			background: url($path-images + "backgrounds/textures/marble-texture-bg.avif")
				repeat center;
			background-size: 40%;
			width: 100%;
			height: 100%;
			content: "";
		}

		&__content {
			@include flex-col-center;
			position: relative;
			gap: 0.875rem;
			padding: 1.5rem 1rem 1rem;
			width: 100%;
			min-height: 3.813rem;
		}

		&__row {
			@include flex-row-center;
			flex-wrap: wrap;
			gap: 0.2rem;
		}

		&--completed {
			margin-bottom: 0;
			box-shadow: inset 0 0 2.375rem 0rem
				rgba($color: #2bad50, $alpha: 0.7);
		}

		.rewards-label {
			position: absolute;
			top: -1rem;
			z-index: 1;
			width: auto;
			height: 2rem;
		}

		.resource_income {
			color: #f2dcb5;
			line-height: 1rem;
			text-shadow: 0.044rem 0.044rem 0.063rem rgba(0, 0, 0, 0.6);
		}

		:deep(.bidirectional-text-label) {
			filter: drop-shadow(0 0 0.25rem rgba(0, 0, 0, 0.6));
		}

		:deep(.price__value) {
			color: #f2dcb5 !important;
		}
	}
}
</style>
